import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Yet another hard lesson about time",
  "description": "",
  "published": "2017-01-19T00:00:00.000Z",
  "redirect_from": ["/blog/another-hard-lesson-time/swizec/7386"],
  "categories": "Learning, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <blockquote>
      <p parentName="blockquote">{`Yo Swiz, some users complained their exam ends immediately.`}</p>
      <p parentName="blockquote">{`Hm… that's weird ?`}</p>
    </blockquote>
    <p>{`So began my two day hunt for a bug.`}</p>
    <p>{`To become a `}<a parentName="p" {...{
        "href": "https://www.yup.com/become-tutor"
      }}>{`Yup tutor`}</a>{`, you have to pass a subject exam: 25 questions about math, chemistry, or physics in 45 minutes. It's hard as hell; I tried.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/01/exam-timer.gif",
        "alt": null
      }}></img></p>
    <p>{`There's a timer in the corner ticking down. 45 minutes, and then you're done. A dialog shows up telling you time’s up. Easy peasy to build, right? Get a start time, add 45 minutes, count down. When timer hits zero, stop. 90% of the time, it works every time.`}</p>
    <p>{`And then there’re `}<em parentName="p">{`those`}</em>{` 7 people. For them, the timer hits zero 1 second after it starts. 45 minutes. Tick. Zero.`}</p>
    <p>{`o.O`}</p>
    <p>{`I looked through the code. No race conditions. You click Start, the server creates an exam, makes a `}<inlineCode parentName="p">{`started_at`}</inlineCode>{` timestamp, adds 45 minutes, and tells the frontend when your exam should end. On every `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/modeling-time-react/swizec/7200"
      }}>{`Clock tick`}</a>{`, the counter updates ,and a MobX observer checks if you're out of time.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/01/time-diagram-1.jpg",
        "alt": null
      }}></img></p>
    <p>{`Very straightforward.`}</p>
    <p>{`We're using React and MobX, so the frontend behaves like a state machine. There's no way we're rendering stale views, no way for poorly garbage collected views to cause problems, and the more I played with the frontend state machine, the sturdier it looked.`}</p>
    <p>{`Here's an excerpt:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class SubjectExam {
  constructor(user, subject) {
    when(
      () => this.isOutOfTime,
      () => {
        if (!this.finished) {
          this.finish();
        }
      }
    );
  }

  @computed get isOutOfTime() {
    return !_.isNull(this.timeLeft) && this.timeLeft <= 0;
  }

  @computed get timeLeft() {
    if (this.should_end_by) {
      return moment.duration(moment.unix(this.should_end_by).diff(Clock.time));
    } else {
      return null;
    }
  }
}
`}</code></pre>
    <p>{`MobX's `}<inlineCode parentName="p">{`when`}</inlineCode>{` creates an autorun that checks the condition function, `}<inlineCode parentName="p">{`() => this.isOutOfTime`}</inlineCode>{`, every time its value changes. When it evaluates to `}<inlineCode parentName="p">{`true`}</inlineCode>{`, it runs the given function. In this case, that’s an action that finishes the subject exam.`}</p>
    <p>{`The reason this works is hidden in `}<inlineCode parentName="p">{`@computed timeLeft()`}</inlineCode>{`. It depends on `}<inlineCode parentName="p">{`Clock.time`}</inlineCode>{`, which is a global MobX store that updates every second. It’s based on my `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/modeling-time-react/swizec/7200"
      }}>{`Modeling time in React & MobX`}</a>{` approach.`}</p>
    <p>{`Diffing the server-provided `}<inlineCode parentName="p">{`should_end_by`}</inlineCode>{` timestamp and `}<inlineCode parentName="p">{`Clock.time`}</inlineCode>{` tells us how much time is left. It works great and produces a countdown timer when you add some rendering.`}</p>
    <p>{`The nice feature of this approach is that the timer is stable. You can refresh and navigate the page as much as you want. The server calculates the time, so it doesn't care.`}</p>
    <p>{`But… those 7 people… why was the time up after 1 second? Can you guess?`}</p>
    <p>{`I found no pattern in who the applicants were, when they took the exam, what timezone they were in, or which browser they used. It's not a bug in MomentJS. None of the cases happened around a DST, day, month, or year boundary.`}</p>
    <p><em parentName="p">{`It should work.`}</em></p>
    <p>{`?`}</p>
    <p>{`The only remaining explanation is that their system time was waaaaaayyy in the future. More than 45 minutes. I don't know why their computers thought they were in the future, but it's the only explanation that works.`}</p>
    <p>{`How do you fix it?`}</p>
    <p>{`You don't rely on user time. Instead of calculating a `}<inlineCode parentName="p">{`should_end_by`}</inlineCode>{` timestamp on the server, calculate the delta. Look only at the delta when doing a countdown.`}</p>
    <p>{`Like this:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/01/time-diagram-2.jpg",
        "alt": null
      }}></img></p>
    <p>{`The exam starts, the server looks at current time and returns `}<inlineCode parentName="p">{`45min`}</inlineCode>{`. User reloads, server looks at current time, calculates delta from start time, returns new time remaining.`}</p>
    <p>{`On the frontend, you create a local `}<inlineCode parentName="p">{`shouldEndAt`}</inlineCode>{` timestamp when you load the exam – `}<inlineCode parentName="p">{`time + delta_from_server`}</inlineCode>{` – then count down from there.`}</p>
    <p>{`Lesson learned. Time is so hard that you can't even rely on people's clocks being accurate to within 1 hour. Add it to the `}<a parentName="p" {...{
        "href": "http://infiniteundo.com/post/25326999628/falsehoods-programmers-believe-about-time"
      }}>{`Falsehoods Programmers Believe About Time`}</a>{` pile.`}</p>
    <p>{`?`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      